import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/shared/lib/api-client'
import { serverReachability } from '@/shared/lib/server-reachability'
import { DURABLE_QUERY } from '@/shared/lib/query-profiles'
import { useJudgeState } from '@/features/live/api'
import type { Score } from '@app/shared/types/entities'

/**
 * Prefetches my-score for ALL students in the judge's assigned categories.
 * This ensures the judge can navigate to any student offline.
 *
 * Re-runs when judge-state data arrives (handles the case where
 * judge-state isn't cached yet on first mount).
 *
 * Call once in JudgeLayout.
 */
export function useScorePrefetch(competitionId: string): void {
  const queryClient = useQueryClient()
  const { data: judgeState } = useJudgeState(competitionId)

  useEffect(() => {
    if (serverReachability.getState() !== 'reachable') return
    if (!judgeState) return

    // Extract all participation IDs from all assigned categories
    for (const category of judgeState.categories) {
      for (const participant of category.participants) {
        const pid = participant.participationId
        queryClient.prefetchQuery({
          queryKey: ['my-score', pid],
          queryFn: async () => {
            const res = await apiClient.get<{ score: Score }>(`/api/participations/${pid}/my-score`)
            return res.score
          },
          ...DURABLE_QUERY,
        })
      }
    }
  }, [judgeState, queryClient])
}
